/**
 * Chat header — top bar of /chat/:channelId.
 *
 * Figma evidence:
 * - S014 default conversation header:  R4VYIdXEv7HYIXtpDMEvHY:38:3425
 *   (the frame is the ChatHeader only; the conversation body is provided
 *   by `ChatView`'s channel-detail branch).
 *
 * Bucket C (ACT-INDEEP-FIGMA-DIRECT-CHAT-BACKEND-SLICE): the header
 * now takes a typed `counterpart` projection (S014 minimal public
 * fields: photo_url, display_name, age, city_or_region). The visible
 * title is rendered from `counterpart.displayName`. Existing testids
 * (chat-header, back-button, realtime-status-*) are preserved.
 *
 * Extraction scope (Phase 3): zero behavior change for the back/title
 * surface. The optional `realtimeStatus` prop is added during the
 * remote-main reconciliation ACT so that the previously inlined
 * realtime-aware header (the local `1771c87` commit "fix(realtime):
 * terminate sockets and revalidate sessions") continues to render the
 * `realtime-status-*` testid without re-introducing a duplicate header
 * definition in `view.tsx`. The `RealtimeStatus` type is imported from
 * `./model` (canonical) so this file does not redefine it.
 */
import type { RealtimeStatus } from '../model';

/**
 * Minimal S014 counterpart projection. Server-authoritative; the same
 * shape as the chat counterpart response field (api/chat.ts
 * ChatCounterpartSummary). photo_url is always null until durable
 * multi-photo storage lands (B-2).
 */
export interface ChatCounterpartSummary {
  readonly profileId: string;
  readonly displayName: string;
  readonly age?: number;
  readonly cityOrRegion?: string;
  readonly photoUrl?: string | null;
}

export interface ChatHeaderProps {
  readonly counterpart: ChatCounterpartSummary;
  readonly onBack?: () => void;
  readonly showBack?: boolean;
  readonly realtimeStatus?: RealtimeStatus;
}

function realtimeBadgeFor(status: RealtimeStatus): string {
  switch (status) {
    case 'connected':
      return 'Live';
    case 'connecting':
      return 'Connecting…';
    case 'reconnecting':
      return 'Reconnecting…';
    case 'disconnected':
    default:
      return 'Offline';
  }
}

export function ChatHeader({
  counterpart,
  onBack,
  showBack = false,
  realtimeStatus,
}: ChatHeaderProps) {
  const badge = realtimeStatus ? realtimeBadgeFor(realtimeStatus) : null;
  const title = counterpart.displayName || 'Conversation';
  const subtitleParts: string[] = [];
  if (typeof counterpart.age === 'number' && counterpart.age > 0) {
    subtitleParts.push(String(counterpart.age));
  }
  if (counterpart.cityOrRegion) {
    subtitleParts.push(counterpart.cityOrRegion);
  }
  const subtitle = subtitleParts.join(' • ');
  return (
    <div className="chat-header" data-testid="chat-header">
      {showBack && (
        <button className="back-button" onClick={onBack} data-testid="back-button">
          ‹ Back
        </button>
      )}
      <h1 className="chat-title" data-testid="chat-counterpart-name">
        {title}
      </h1>
      {subtitle && (
        <span className="chat-subtitle" data-testid="chat-counterpart-subtitle">
          {subtitle}
        </span>
      )}
      {badge && (
        <span
          className={`realtime-status realtime-status-${realtimeStatus}`}
          data-testid={`realtime-status-${realtimeStatus}`}
        >
          {badge}
        </span>
      )}
    </div>
  );
}